import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Bookmark, Loader2, Save } from 'lucide-react';
import {
  listTemplates,
  listPersonas,
  createTemplate,
  createPersona,
  type Persona,
  type PromptTemplate,
} from '@/services/prompts-api';
import { AccountPicker } from '@/components/prompts/AccountPicker';

export const PLATFORM_OPTIONS = [
  { value: 'tiktok', label: 'TikTok' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'reddit', label: 'Reddit' },
] as const;

/**
 * Snapshot of the run/schedule form fields a saved prompt can capture. Both the
 * Runs page and ScheduleSheet expose exactly these, so the panel is shared.
 */
export interface RunFormSnapshot {
  task: string;
  persona: string;
  brandId: string; // '' = none (Runs page) or '_none' (ScheduleSheet sentinel)
  llmModel: string;
  commentLlmModel: string;
  maxSteps: number;
  disableProactiveSearch: boolean;
  captureRequired: boolean;
  returnHomeOnFinish: boolean;
  maxCommentsPerSession: number | '';
  maxSearchesPerSession: number | '';
}

function brandToApi(brandId: string): string | null {
  return !brandId || brandId === '_none' ? null : brandId;
}

function numToApi(v: number | ''): number | null {
  return v === '' ? null : v;
}

interface SavedPromptsPanelProps {
  values: RunFormSnapshot;
  onApplyTemplate: (t: PromptTemplate) => void;
  onApplyPersona: (p: Persona) => void;
  onAppliedTemplateChange: (id: string | null) => void;
  disabled?: boolean;
  /** Currently selected devices — used to filter the dropdowns + warn on mismatch. */
  selectedPadIds?: string[];
}

export function SavedPromptsPanel({
  values,
  onApplyTemplate,
  onApplyPersona,
  onAppliedTemplateChange,
  disabled = false,
  selectedPadIds,
}: SavedPromptsPanelProps) {
  const qc = useQueryClient();
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [showSavePersona, setShowSavePersona] = useState(false);

  const { data: templates = [] } = useQuery({
    queryKey: ['prompt-templates'],
    queryFn: () => listTemplates(),
    staleTime: 30_000,
  });
  const { data: personas = [] } = useQuery({
    queryKey: ['personas'],
    queryFn: () => listPersonas(),
    staleTime: 30_000,
  });

  const saveTemplateMut = useMutation({
    mutationFn: (payload: Parameters<typeof createTemplate>[0]) => createTemplate(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['prompt-templates'] });
      toast.success('Prompt saved');
      setShowSaveTemplate(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const savePersonaMut = useMutation({
    mutationFn: (payload: Parameters<typeof createPersona>[0]) => createPersona(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['personas'] });
      toast.success('Persona saved');
      setShowSavePersona(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const canSaveTemplate = values.task.trim().length > 0;
  const canSavePersona = values.persona.trim().length > 0;

  const selectedSet = new Set(selectedPadIds ?? []);

  const visibleTemplates = useMemo(() => {
    const sel = new Set(selectedPadIds ?? []);
    return templates.filter((t) => {
      const padIds = (t.account_overrides ?? []).map((o) => o.pad_id);
      return sel.size === 0 || padIds.length === 0 || padIds.some((p) => sel.has(p));
    });
  }, [templates, selectedPadIds]);
  const visiblePersonas = useMemo(() => {
    const sel = new Set(selectedPadIds ?? []);
    return personas.filter((p) => {
      const padIds = p.account_pad_ids ?? [];
      return sel.size === 0 || padIds.length === 0 || padIds.some((x) => sel.has(x));
    });
  }, [personas, selectedPadIds]);

  const warnMismatch = (name: string, scopedPadIds: string[]) => {
    if (selectedSet.size === 0 || scopedPadIds.length === 0) return;
    if (!scopedPadIds.some((p) => selectedSet.has(p))) {
      toast.warning(`"${name}" is scoped to other accounts — check your selected devices.`);
    }
  };

  return (
    <div className="rounded-lg border bg-muted/20 p-2.5 space-y-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          disabled={disabled || visibleTemplates.length === 0}
          onValueChange={(id) => {
            const t = templates.find((x) => x.id === id);
            if (t) {
              onApplyTemplate(t);
              onAppliedTemplateChange(t.id);
              warnMismatch(t.name, (t.account_overrides ?? []).map((o) => o.pad_id));
              toast.success(`Loaded "${t.name}"`);
            }
          }}
        >
          <SelectTrigger className="h-8 w-auto min-w-[220px] flex-1 text-xs">
            <SelectValue placeholder={visibleTemplates.length === 0 ? 'No saved prompts yet' : 'Load a saved prompt…'} />
          </SelectTrigger>
          <SelectContent>
            {visibleTemplates.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
                {t.platform ? ` · ${t.platform}` : ''}
                {t.usage_count > 0 ? ` · used ${t.usage_count}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1.5 ml-auto">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            disabled={disabled || !canSaveTemplate}
            title={canSaveTemplate ? 'Save the current task + persona + settings as a reusable prompt' : 'Enter a task first'}
            onClick={() => setShowSaveTemplate(true)}
          >
            <Save className="h-3.5 w-3.5 mr-1.5" />
            Save as prompt
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            disabled={disabled || !canSavePersona}
            title={canSavePersona ? 'Save the persona box as a reusable persona' : 'Enter a persona first'}
            onClick={() => setShowSavePersona(true)}
          >
            <Bookmark className="h-3.5 w-3.5 mr-1.5" />
            Save persona
          </Button>
        </div>
      </div>

      {visiblePersonas.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground shrink-0">
            Persona library
          </span>
          <Select
            disabled={disabled}
            onValueChange={(id) => {
              const p = personas.find((x) => x.id === id);
              if (p) {
                onApplyPersona(p);
                warnMismatch(p.name, p.account_pad_ids ?? []);
                toast.success(`Applied persona "${p.name}"`);
              }
            }}
          >
            <SelectTrigger className="h-7 w-auto min-w-[200px] flex-1 text-xs">
              <SelectValue placeholder="Apply a saved persona to the Persona box…" />
            </SelectTrigger>
            <SelectContent>
              {visiblePersonas.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                  {p.usage_count > 0 ? ` · used ${p.usage_count}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {showSaveTemplate && (
        <SaveTemplateDialog
          isSubmitting={saveTemplateMut.isPending}
          onClose={() => setShowSaveTemplate(false)}
          onSubmit={(name, platform, accountPadIds) =>
            saveTemplateMut.mutate({
              name,
              platform: platform || null,
              task: values.task.trim(),
              persona_text: values.persona.trim() || null,
              brand_id: brandToApi(values.brandId),
              llm_model: 'gemini-3-flash-preview',
              comment_llm_model: 'gemini-3-flash-preview',
              max_steps: values.maxSteps,
              disable_proactive_search: values.disableProactiveSearch,
              capture_required: values.captureRequired,
              return_home_on_finish: values.returnHomeOnFinish,
              max_comments_per_session: numToApi(values.maxCommentsPerSession),
              max_searches_per_session: numToApi(values.maxSearchesPerSession),
              account_overrides: accountPadIds.map((pad_id) => ({ pad_id })),
            })
          }
        />
      )}

      {showSavePersona && (
        <SavePersonaDialog
          isSubmitting={savePersonaMut.isPending}
          initialPersonaText={values.persona}
          onClose={() => setShowSavePersona(false)}
          onSubmit={(name, personaText, accountPadIds) =>
            savePersonaMut.mutate({ name, persona_text: personaText, account_pad_ids: accountPadIds })
          }
        />
      )}
    </div>
  );
}

function SaveTemplateDialog({
  isSubmitting,
  onClose,
  onSubmit,
}: {
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (name: string, platform: string | null, accountPadIds: string[]) => void;
}) {
  const [name, setName] = useState('');
  const [platform, setPlatform] = useState<string>('none');
  const [accountPadIds, setAccountPadIds] = useState<string[]>([]);
  const valid = name.trim().length > 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Save as prompt</DialogTitle>
          <DialogDescription>
            Captures the task, persona, brand, models, and limits so you can re-run this
            engagement workflow (or schedule it) without pasting it again.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="template-name">Name</Label>
            <Input
              id="template-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Grove — full engagement on Facebook Groups"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label>Platform (optional tag)</Label>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Any / unspecified</SelectItem>
                {PLATFORM_OPTIONS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Accounts (optional)</Label>
            <AccountPicker value={accountPadIds} onChange={setAccountPadIds} />
            <p className="text-[11px] text-muted-foreground">Leave empty for any account.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit(name.trim(), platform === 'none' ? null : platform, accountPadIds)}
            disabled={!valid || isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Save prompt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SavePersonaDialog({
  isSubmitting,
  initialPersonaText,
  onClose,
  onSubmit,
}: {
  isSubmitting: boolean;
  initialPersonaText: string;
  onClose: () => void;
  onSubmit: (name: string, personaText: string, accountPadIds: string[]) => void;
}) {
  const [name, setName] = useState('');
  const [personaText, setPersonaText] = useState(initialPersonaText);
  const [accountPadIds, setAccountPadIds] = useState<string[]>([]);
  const valid = name.trim().length > 0 && personaText.trim().length > 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Save persona</DialogTitle>
          <DialogDescription>
            Saves the persona text so you can apply it to any run or schedule (and attach it to
            saved prompts) without retyping it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="persona-name">Name</Label>
            <Input
              id="persona-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Grove commenter — casual expert"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="persona-text">Persona text</Label>
            <Textarea
              id="persona-text"
              value={personaText}
              onChange={(e) => setPersonaText(e.target.value)}
              rows={6}
              className="font-mono text-xs"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Accounts (optional)</Label>
            <AccountPicker value={accountPadIds} onChange={setAccountPadIds} />
            <p className="text-[11px] text-muted-foreground">Leave empty for any account.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit(name.trim(), personaText.trim(), accountPadIds)}
            disabled={!valid || isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Bookmark className="h-4 w-4 mr-1" />
            )}
            Save persona
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
